import { useCurrentSubscription } from '@api/hooks/useSubscription';
import { usePaymentMethod } from '@api/hooks/usePaymentMethod';
import { useBillingPortal } from '@api/hooks/useBillingPortal';
import Button from '@components/common/Button';
import { Card, CardTitle } from '@components/common/Card';
import { formatDate } from '@utils/formatters';

export function BillingSettings() {
  const sub = useCurrentSubscription();
  const pm = usePaymentMethod();
  const portal = useBillingPortal();

  if (sub.isLoading || pm.isLoading) {
    return <div className="p-6 text-content-secondary">Loading...</div>;
  }

  if (!sub.data) {
    return <div className="p-6 text-content-secondary">Could not load billing.</div>;
  }

  const s = sub.data;

  const manage = async () => {
    try {
      const res = await portal.mutateAsync(
        `${window.location.origin}/admin/settings?tab=billing`,
      );
      window.location.href = res.url;
    } catch {
      // Errors surface via the global notification interceptor.
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-content-primary">Tenant Billing</h2>
        <p className="mt-1 text-sm text-content-secondary">
          Manage the active tenant's platform subscription, billing method, and promotions.
        </p>
      </div>

      <Card>
        <div className="space-y-2">
          <CardTitle className="text-xl">Plan</CardTitle>
          <p className="text-sm text-content-secondary">
            {s.plan.display_name}&nbsp;-&nbsp;
            <span className="font-mono text-xs text-content-primary">{s.status}</span>
          </p>
          {s.entitlements?.amount_cents != null && (
            <p className="text-sm text-content-secondary">
              Monthly: ${(s.entitlements.amount_cents / 100).toFixed(2)}
            </p>
          )}
          {s.trial_ends_at && (
            <p className="text-sm text-content-secondary">
              Trial ends: {formatDate(s.trial_ends_at)}
            </p>
          )}
          {s.current_period_end && (
            <p className="text-sm text-content-secondary">
              Next billing: {formatDate(s.current_period_end)}
            </p>
          )}
        </div>
      </Card>

      <Card>
        <div className="space-y-3">
          <CardTitle className="text-xl">Payment Method</CardTitle>
          {pm.data?.has_payment_method ? (
            <p className="text-sm text-content-secondary">
              {pm.data.brand?.toUpperCase()} **** {pm.data.last4}
              {' '}(expires {pm.data.exp_month}/{pm.data.exp_year})
            </p>
          ) : (
            <p className="text-sm text-content-secondary">No payment method on file.</p>
          )}
          <Button
            onClick={() => void manage()}
            disabled={portal.isPending}
            loading={portal.isPending}
            size="sm"
          >
            {portal.isPending ? 'Opening...' : 'Manage billing'}
          </Button>
        </div>
      </Card>

      {s.promotion && (
        <Card>
          <div className="space-y-1">
            <CardTitle className="text-xl">Promotion Applied</CardTitle>
            <p className="text-sm text-content-secondary">
              {s.promotion.display_name} (code:{' '}
              <code className="font-mono text-xs">{s.promotion.code}</code>)
            </p>
            {s.promotion.discount_summary && (
              <p className="text-xs text-content-muted">{s.promotion.discount_summary}</p>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
